import { ExternalLink, KeyRound } from "lucide-react";

import { EntityPreview, entityPreviewIconType } from "../../utils/entityPreview";

import { useWorkspaceToolNavigation } from "./useWorkspaceToolNavigation";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { cn } from "@/lib/utils";

interface EntityPreviewCardProps {
  preview: EntityPreview;
}

const buildTooltipText = (preview: EntityPreview): string => {
  const parts = [preview.typeLabel, preview.subtitle, preview.description].filter(
    (part): part is string => Boolean(part && part.trim()),
  );
  return parts.join(" · ");
};

export const EntityPreviewCard = ({ preview }: EntityPreviewCardProps) => {
  const handleNavigate = useWorkspaceToolNavigation();
  const entityType = entityPreviewIconType(preview);
  const isNavigable = Boolean(preview.url);
  const tooltipText = buildTooltipText(preview);

  const row = (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
      <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
        {entityType ? (
          <EntityIcon
            type={entityType}
            size="xs"
            className="text-gray-400 transition-colors group-hover:text-gray-500"
          />
        ) : (
          <KeyRound className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-gray-500" />
        )}
      </span>
      <span className="min-w-0 truncate text-xs text-gray-500 transition-colors group-hover:text-gray-700">
        <span className="font-medium text-gray-600 transition-colors group-hover:text-gray-800">
          {preview.typeLabel}:
        </span>{" "}
        {preview.title}
      </span>
      {isNavigable ? (
        <ExternalLink className="h-3 w-3 flex-shrink-0 text-gray-400 transition-colors group-hover:text-gray-600" />
      ) : null}
    </span>
  );

  const className = cn(
    "group -ml-1 inline-flex w-fit max-w-full items-center rounded-md px-1 py-0.5 text-left transition-colors",
    isNavigable &&
      "hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300",
  );

  const trigger =
    isNavigable && preview.url ? (
      <button
        type="button"
        onClick={() => handleNavigate(preview.url!, "push")}
        className={className}
      >
        {row}
      </button>
    ) : (
      <div className={className}>{row}</div>
    );

  if (!tooltipText) {
    return trigger;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
